import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Container, Reveal, SectionLabel } from "../components/primitives";
import Seo from "../components/Seo";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

const YESNO = [
  ["wants_calls", "Do you want customers to call you?"],
  ["wants_quotes", "Do you want customers to request a quote?"],
  ["wants_booking", "Do you want customers to book appointments?"],
  ["needs_payments", "Do you need to take payments online?"],
  ["has_logo", "Do you have a logo?"],
  ["has_photos", "Do you have professional photos?"],
];

export default function Advisor() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ has_website: "no" });
  const [loading, setLoading] = useState(false);
  const [rec, setRec] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.post("/track", { event: "advisor_started" }).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const getRecommendation = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/advisor/recommend", form);
      setRec(data);
      setChosen(data.package);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const saveLead = async () => {
    if (!contact.first_name || !contact.email) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      await api.post("/advisor/lead", {
        ...contact, business_name: form.business_name, website: form.existing_website_url,
        business_type: form.business_type, recommended_package: chosen,
      });
      toast.success("Saved! Check your email — we'll be in touch.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const goCheckout = async () => {
    const alt = rec.alternatives.find((a) => a.package === chosen) || rec.alternatives.find((a) => a.key === rec.recommended_key);
    const lookup_key = alt?.key || rec.recommended_key;
    await api.post("/track", { event: "advisor_to_checkout", meta: { package: chosen } }).catch(() => {});
    localStorage.setItem("wls_intended_plan", lookup_key);
    if (!user) { toast.info("Create an account to continue to checkout"); navigate("/register"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setLoading(false); }
  };

  return (
    <div data-testid="advisor-page" className="pt-32 pb-24 min-h-screen">
      <Seo page="pricing" />
      <Container>
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <SectionLabel><Sparkles className="inline h-3.5 w-3.5 mr-1" /> AI Website Advisor</SectionLabel>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tighter text-[#F3EDDC]">Build my website</h1>
            <p className="mt-4 text-lg text-[#B7AF98]">Answer a few quick questions and we'll recommend the perfect package for your business.</p>
          </div>
        </Reveal>

        <div className="mt-12 max-w-2xl mx-auto">
          {step < 3 && (
            <div className="mb-6 flex items-center gap-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#C9A227]" : "bg-white/10"}`} />
              ))}
            </div>
          )}

          <div className="rounded-3xl border border-white/10 bg-[#141414] p-8">
            {step === 0 && (
              <div className="space-y-5" data-testid="advisor-step-business">
                <h2 className="font-display text-lg font-semibold text-[#F3EDDC]">About your business</h2>
                <div className="space-y-1.5"><Label className="text-[#D2CAB2]">Business name</Label><Input data-testid="advisor-business-name" value={form.business_name || ""} onChange={set("business_name")} /></div>
                <div className="space-y-1.5"><Label className="text-[#D2CAB2]">What type of business do you have?</Label><Input data-testid="advisor-business-type" value={form.business_type || ""} onChange={set("business_type")} placeholder="e.g. Roofing, Restaurant, Med Spa" /></div>
                <div className="space-y-1.5"><Label className="text-[#D2CAB2]">What city/state do you serve?</Label><Input data-testid="advisor-location" value={form.location || ""} onChange={set("location")} /></div>
                <div className="space-y-1.5">
                  <Label className="text-[#D2CAB2]">Do you currently have a website?</Label>
                  <div className="flex gap-2">
                    {["no", "yes"].map((v) => (
                      <button key={v} type="button" data-testid={`advisor-haswebsite-${v}`} onClick={() => setForm((f) => ({ ...f, has_website: v }))}
                        className={`px-4 py-2 rounded-full text-sm capitalize border ${form.has_website === v ? "bg-[#C9A227] text-black border-[#C9A227]" : "border-white/15 text-[#D2CAB2]"}`}>{v}</button>
                    ))}
                  </div>
                </div>
                {form.has_website === "yes" && (
                  <div className="space-y-1.5"><Label className="text-[#D2CAB2]">Your current website URL</Label><Input data-testid="advisor-existing-url" value={form.existing_website_url || ""} onChange={set("existing_website_url")} placeholder="https://" /></div>
                )}
                <div className="flex justify-end"><Button data-testid="advisor-next-0" onClick={() => setStep(1)} className="rounded-full bg-[#C9A227] text-black">Next <ArrowRight className="ml-1 h-4 w-4" /></Button></div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5" data-testid="advisor-step-goals">
                <h2 className="font-display text-lg font-semibold text-[#F3EDDC]">Your services & goals</h2>
                <div className="space-y-1.5"><Label className="text-[#D2CAB2]">What services do you offer?</Label><Textarea data-testid="advisor-services" value={form.services || ""} onChange={set("services")} rows={2} placeholder="Separate with commas" /></div>
                <div className="space-y-1.5"><Label className="text-[#D2CAB2]">What's the main purpose of your website?</Label><Input data-testid="advisor-purpose" value={form.purpose || ""} onChange={set("purpose")} placeholder="e.g. Get more leads, take bookings, refresh an old site" /></div>
                <div className="grid grid-cols-1 gap-3">
                  {YESNO.map(([k, q]) => (
                    <div key={k} className="flex items-center justify-between gap-3">
                      <span className="text-sm text-[#D2CAB2]">{q}</span>
                      <div className="flex gap-2">
                        {[["Yes", true], ["No", false]].map(([lbl, v]) => (
                          <button key={lbl} type="button" data-testid={`advisor-${k}-${lbl.toLowerCase()}`} onClick={() => setBool(k, v)}
                            className={`px-3 py-1 rounded-full text-xs border ${form[k] === v ? "bg-[#C9A227] text-black border-[#C9A227]" : "border-white/15 text-[#948C79]"}`}>{lbl}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(0)} className="rounded-full"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
                  <Button data-testid="advisor-next-1" onClick={() => setStep(2)} className="rounded-full bg-[#C9A227] text-black">Next <ArrowRight className="ml-1 h-4 w-4" /></Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5" data-testid="advisor-step-budget">
                <h2 className="font-display text-lg font-semibold text-[#F3EDDC]">Budget</h2>
                <div className="space-y-1.5"><Label className="text-[#D2CAB2]">What's your approximate budget?</Label>
                  <div className="flex flex-wrap gap-2">
                    {["Under $500", "$500–$1,000", "$1,000–$1,500", "$1,500+"].map((b) => (
                      <button key={b} type="button" data-testid={`advisor-budget-${b}`} onClick={() => setForm((f) => ({ ...f, budget: b }))}
                        className={`px-4 py-2 rounded-full text-sm border ${form.budget === b ? "bg-[#C9A227] text-black border-[#C9A227]" : "border-white/15 text-[#D2CAB2]"}`}>{b}</button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5"><Label className="text-[#D2CAB2]">Who are your ideal customers? (optional)</Label><Input data-testid="advisor-ideal" value={form.ideal_customers || ""} onChange={set("ideal_customers")} /></div>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)} className="rounded-full"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
                  <Button data-testid="advisor-get-recommendation" onClick={getRecommendation} disabled={loading} className="rounded-full gold-shimmer-btn bg-[#C9A227] text-black">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Get my recommendation <Sparkles className="ml-1 h-4 w-4" /></>}
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && rec && (
              <div className="space-y-6" data-testid="advisor-result">
                <div className="text-center">
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#C9A227]/15 text-[#C9A227] px-3 py-1 text-xs font-semibold">Recommended for you</span>
                  <h2 className="mt-3 font-display text-3xl font-bold text-[#F3EDDC]" data-testid="advisor-rec-name">{rec.package_name} — ${rec.price}</h2>
                  <p className="mt-3 text-[#B7AF98] leading-relaxed" data-testid="advisor-rec-reasoning">{rec.reasoning}</p>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-widest text-[#7E7663] mb-2">Choose a package</div>
                  <div className="grid grid-cols-2 gap-2">
                    {rec.alternatives.map((a) => (
                      <button key={a.package} type="button" data-testid={`advisor-choose-${a.package}`} onClick={() => setChosen(a.package)}
                        className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm ${chosen === a.package ? "border-[#C9A227] bg-[#C9A227]/10 text-[#F3EDDC]" : "border-white/10 text-[#D2CAB2]"}`}>
                        <span>{a.name}</span>
                        <span className="flex items-center gap-1">${a.price}{chosen === a.package && <Check className="h-4 w-4 text-[#C9A227]" />}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <Button data-testid="advisor-checkout-btn" onClick={goCheckout} disabled={loading} className="w-full rounded-full h-12 gold-shimmer-btn bg-[#C9A227] text-black">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue to secure checkout <ArrowRight className="ml-1 h-4 w-4" /></>}
                </Button>

                <div className="rounded-2xl border border-white/10 p-5">
                  <div className="text-sm text-[#D2CAB2] mb-3">Not ready yet? Save your recommendation and we'll follow up.</div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input data-testid="advisor-lead-firstname" placeholder="First name *" value={contact.first_name} onChange={(e) => setContact({ ...contact, first_name: e.target.value })} />
                    <Input data-testid="advisor-lead-lastname" placeholder="Last name" value={contact.last_name} onChange={(e) => setContact({ ...contact, last_name: e.target.value })} />
                    <Input data-testid="advisor-lead-email" placeholder="Email *" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                    <Input data-testid="advisor-lead-phone" placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                  </div>
                  <Button data-testid="advisor-lead-save" onClick={saveLead} disabled={saving} variant="outline" className="mt-3 w-full rounded-full">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email me my recommendation"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
}
